import { isCalendly, type TCalendlyQuestion, type TSchedulingQuestion } from '@hubertai/screening-question-types';
import { CalendarClock, Globe, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { resolveLocalized } from '../../AgentChat/chat/utils/templatePreviewWithSuggestion.utils';

interface BookingPreviewProps {
  question: TSchedulingQuestion<string> | TCalendlyQuestion<string>;
  language: string;
}

/**
 * Capability card for the two booking question types.
 *
 * Same reasoning as the recording card: a real preview would mount a third-party booking widget
 * (cal.com or Calendly) in the builder, loading live availability for a real calendar — slow,
 * noisy, and it shows the recruiter nothing they configured. What they DID configure is the
 * event, the link and the timezone, none of which the embed displays. So the card states those.
 *
 * Calendly is legacy — it is absent from CreateableQuestionTypes and has no builder editor, so no
 * new ones can be authored. It is handled here only so existing templates do not preview blank.
 *
 * NOTE: `calendlyPersonalAccessToken` is a per-question SECRET and is deliberately never rendered.
 */
export const BookingPreview = ({ question, language }: BookingPreviewProps) => {
  const { t } = useTranslation('Questionsv2');

  if (isCalendly(question)) {
    const url = resolveLocalized(question.calendlyUrl, language) ?? '';
    return (
      <Card title={t('questionPreview.booking.calendlyTitle')} legacyNote={t('questionPreview.booking.calendlyLegacy')}>
        {url ? <Row icon={Link2} value={url} /> : null}
      </Card>
    );
  }

  const { provider, event_type_id: eventTypeId, cal_link: calLink, timezone } = question.scheduling;

  return (
    <Card title={t('questionPreview.booking.schedulingTitle', { provider })}>
      {calLink ? <Row icon={Link2} value={calLink} /> : null}
      {eventTypeId != null ? (
        <Row icon={CalendarClock} value={`${t('questionPreview.booking.eventType')} ${eventTypeId}`} />
      ) : null}
      {timezone ? <Row icon={Globe} value={timezone} /> : null}
    </Card>
  );
};

const Card = ({ title, legacyNote, children }: { title: string; legacyNote?: string; children: React.ReactNode }) => {
  const { t } = useTranslation('Questionsv2');
  return (
    <div className="flex max-w-md flex-col gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      <div className="flex items-center gap-2">
        <CalendarClock size={16} className="shrink-0 text-violet-600" />
        <span className="text-xs font-semibold text-slate-700">{title}</span>
      </div>
      <div className="flex flex-col gap-1">{children}</div>
      {legacyNote ? <p className="text-[11px] text-amber-600">{legacyNote}</p> : null}
      <p className="text-[11px] text-slate-400">{t('questionPreview.booking.notPreviewable')}</p>
    </div>
  );
};

const Row = ({ icon: Icon, value }: { icon: typeof Link2; value: string }) => (
  <span className="flex items-center gap-1.5 text-[11px] text-slate-600">
    <Icon size={11} className="shrink-0" />
    {/* A link or timezone is data, not chrome — isolate it so it cannot reorder the row */}
    <bdi className="truncate">{value}</bdi>
  </span>
);
